import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import useTypedSelector from 'hooks/useTypedSelector';
import { onDelete, onGetAll } from 'redux/actions/persons.actions';
import { personsSelector } from 'redux/selectors/persons.selector';
import { history } from 'helpers/history';
import { COLORS } from 'helpers/enums/colors';

import LayoutContainer from 'containers/LayoutContainer';
import PersonsView from 'views/PersonsView';
import ActionButtons from 'components/commons/Table/components/ActionButtons';
import Dialog from 'components/commons/Dialog';

import { IPerson } from 'types/persons.types';
import { ITableUpdate } from 'components/commons/Table/types';

import 'views/PersonsView/i18n';

const PersonsContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [deleteItem, setDeleteItem] = useState<IPerson | undefined>();
  const { count, loading, table } = useTypedSelector((state) => state.persons);
  const items = useTypedSelector(personsSelector);

  const actions = [
    {
      label: i18next.t<string>('PERSONS_VIEW:SHOW'),
      onClick: (id: string) => history.push(`/persons/${id}/show`),
    },
    {
      label: i18next.t<string>('PERSONS_VIEW:EDIT'),
      onClick: (id: string) => history.push(`/persons/${id}/edit`),
    },
    {
      label: i18next.t<string>('PERSONS_VIEW:DELETE'),
      onClick: (id: string) => setDeleteItem(items.find((item: IPerson) => item.id === Number(id))),
      color: COLORS.redMonza,
    },
  ];

  const columns = [
    {
      name: 'firstName',
      label: i18next.t<string>('PERSONS_VIEW:FIRST_NAME'),
    },
    {
      name: 'lastName',
      label: i18next.t<string>('PERSONS_VIEW:LAST_NAME'),
    },
    {
      name: 'idCardTypeName',
      label: i18next.t<string>('PERSONS_VIEW:ID_CARD_TYPE'),
    },
    {
      name: 'idCard',
      label: i18next.t<string>('PERSONS_VIEW:ID_CARD'),
    },
    {
      name: 'id',
      label: i18next.t<string>('PERSONS_VIEW:ACTIONS'),
      options: {
        customBodyRender: (value: string) => <ActionButtons id={value} items={actions} />,
        sort: false,
      },
    },
  ];

  const options = {
    setRowProps: (_row: Array<string>, index: number) => ({
      style: { background: items[index].comments ? COLORS.redBittersweet : 'none' },
    }),
  };

  const onTableUpdate = useCallback((data: ITableUpdate) => dispatch(onGetAll(data)), [dispatch]);

  const onDeleteClick = (isConfirmed = false) => {
    if (isConfirmed && deleteItem) dispatch(onDelete(deleteItem.id.toString()));
    setDeleteItem(undefined);
  };

  return (
    <LayoutContainer auth title={i18next.t<string>('PERSONS_VIEW:TITLE')}>
      <PersonsView
        columns={columns}
        count={count}
        data={items}
        loading={loading}
        onAddClick={() => history.push('/persons/create')}
        onTableUpdate={onTableUpdate}
        options={options}
        table={table}
      />
      <Dialog
        message={i18next.t<string>('PERSONS_VIEW:DELETE_DIALOG_MESSAGE', {
          name: `${deleteItem?.firstName} ${deleteItem?.lastName}`,
        })}
        onCancelClick={() => onDeleteClick()}
        onConfirmClick={() => onDeleteClick(true)}
        open={!!deleteItem}
        title={i18next.t<string>('PERSONS_VIEW:DELETE_DIALOG_TITLE')}
      />
    </LayoutContainer>
  );
};

export default PersonsContainer;
